import { useEffect, useMemo, useRef, useState } from 'react'

import { useNavigate } from 'react-router-dom'
import { useCheckTokenQuery, useGetUserQuery } from 'shared/api/auth/requests'
import { useGetPermissions } from 'shared/api/auth/requests/permissions/getPermissions'
import { CustomEvents } from 'shared/constants/customEvents'
import { LocalStorageKey } from 'shared/constants/localStorage'
import { APP_ROUTES } from 'shared/constants/routes'
import { SessionStorageKey } from 'shared/constants/sessionStorage'
import { UserWithToken } from 'shared/types/user'
import {
  dispatchCustomEvent,
  listenCustomEvent
} from 'shared/utils/customEvents'
import { lsGetItem, lsRemoveItem, lsSetItem } from 'shared/utils/local-storage'

import { getIsInsideIframe, isUserAnonymous } from '../utils'
import { useAuthCheckLeader } from './useAuthCheckLeader'

type BootstrapTokens = {
  access_token: string
  refresh_token: string
}

// /checkToken may rotate response metadata without changing the authenticated
// session. Keep this list narrow: every omitted field is intentionally ignored
// only to prevent metadata-only localStorage writes and cross-tab events.
const CHECK_TOKEN_VOLATILE_USER_FIELDS = new Set<keyof UserWithToken>([
  'created_at',
  'updated_at',
  'expires_in',
  'tokenReceiveTime',
  'token_type'
])

const serializeForComparison = (value: unknown) =>
  JSON.stringify(value, (_key, nestedValue) => {
    if (
      nestedValue &&
      typeof nestedValue === 'object' &&
      !Array.isArray(nestedValue)
    ) {
      return Object.keys(nestedValue)
        .sort()
        .reduce<Record<string, unknown>>((sortedValue, key) => {
          sortedValue[key] = (nestedValue as Record<string, unknown>)[key]
          return sortedValue
        }, {})
    }

    return nestedValue
  })

const serializeUserForComparison = (user: UserWithToken | null) => {
  if (!user) return serializeForComparison(user)

  return serializeForComparison(
    Object.fromEntries(
      Object.entries(user).filter(
        ([key]) =>
          !CHECK_TOKEN_VOLATILE_USER_FIELDS.has(key as keyof UserWithToken)
      )
    )
  )
}

const tokenPairsMatch = (
  first: BootstrapTokens | UserWithToken | null,
  second: BootstrapTokens | UserWithToken | null
) =>
  Boolean(
    first &&
      second &&
      first.access_token === second.access_token &&
      first.refresh_token === second.refresh_token
  )

const parseStorageValue = <T>(value: string | null): T | null => {
  if (!value) return null

  try {
    return JSON.parse(value) as T
  } catch {
    return null
  }
}

const removePromotedBootstrapTokens = (
  bootstrapTokens: BootstrapTokens | null
) => {
  if (!bootstrapTokens) return false

  const persistedBootstrapTokens = lsGetItem(LocalStorageKey.BOOTSTRAP_TOKENS)
  const persistedUser = lsGetItem(LocalStorageKey.USER)
  if (
    tokenPairsMatch(bootstrapTokens, persistedBootstrapTokens) &&
    tokenPairsMatch(bootstrapTokens, persistedUser) &&
    persistedUser?.isLoggedIn
  ) {
    return removeCapturedBootstrapTokens(bootstrapTokens)
  }

  return false
}

const removeCapturedBootstrapTokens = (bootstrapTokens: BootstrapTokens) => {
  if (
    !tokenPairsMatch(
      bootstrapTokens,
      lsGetItem(LocalStorageKey.BOOTSTRAP_TOKENS)
    )
  ) {
    return false
  }

  lsRemoveItem(LocalStorageKey.BOOTSTRAP_TOKENS)
  return true
}

const rotateCapturedBootstrapTokens = (
  previousTokens: BootstrapTokens,
  nextTokens: BootstrapTokens
) => {
  if (
    !tokenPairsMatch(
      previousTokens,
      lsGetItem(LocalStorageKey.BOOTSTRAP_TOKENS)
    )
  ) {
    return false
  }

  lsSetItem(LocalStorageKey.BOOTSTRAP_TOKENS, nextTokens)
  return tokenPairsMatch(
    nextTokens,
    lsGetItem(LocalStorageKey.BOOTSTRAP_TOKENS)
  )
}

export const useIsLoggedIn = () => {
  const initialSession = useMemo(() => {
    const storedUser = lsGetItem(LocalStorageKey.USER)
    const storedBootstrapTokens = lsGetItem(LocalStorageKey.BOOTSTRAP_TOKENS)
    const recoverableBootstrapTokens =
      storedBootstrapTokens &&
      (!storedUser || tokenPairsMatch(storedBootstrapTokens, storedUser))
        ? storedBootstrapTokens
        : null

    return {
      user: storedUser,
      bootstrapTokens: recoverableBootstrapTokens
    }
  }, [])
  const [user, setUser] = useState(initialSession.user)
  const navigate = useNavigate()

  const accessTokenRef = useRef<string | null>(
    user?.access_token ?? initialSession.bootstrapTokens?.access_token ?? null
  )
  const refreshTokenRef = useRef<string | null>(
    user?.refresh_token ?? initialSession.bootstrapTokens?.refresh_token ?? null
  )
  const bootstrapSessionRef = useRef(initialSession.bootstrapTokens)
  const [tokenVersion, setTokenVersion] = useState(0) // Force query re-run

  // Tokens minted by a remote's own backend are opaque to the auth service —
  // /checkToken would reject them with a 401, so the query stays disabled and
  // the remote-supplied user is trusted as-is (see the externalUser branch of
  // the AUTH_TOKEN_UPDATED listener below).
  const [isExternalAuth, setIsExternalAuth] = useState(
    () => !!user?.isExternalAuth
  )
  const isAuthCheckLeader = useAuthCheckLeader(!getIsInsideIframe())

  const checkTokenRequestToken = accessTokenRef.current ?? ''
  const { data, dataUpdatedAt, isLoading } = useCheckTokenQuery(
    { token: checkTokenRequestToken },
    {
      refetchInterval: () => {
        return 60 * 1000 * 3
      },
      enabled: isAuthCheckLeader && !!accessTokenRef.current && !isExternalAuth,
      queryKey: [tokenVersion]
    }
  )
  const latestCheckTokenRequestRef = useRef(checkTokenRequestToken)
  const validatedTokenRef = useRef<string | null>(null)
  latestCheckTokenRequestRef.current = checkTokenRequestToken

  // dataUpdatedAt changes even when React Query structurally shares an
  // identical response. Deliberately do not key this effect by the current
  // token: if a storage event changes tokens while old data is still exposed,
  // the response must remain associated with the token that produced it.
  useEffect(() => {
    validatedTokenRef.current = data ? latestCheckTokenRequestRef.current : null
  }, [data, dataUpdatedAt])

  const { data: userDetails } = useGetUserQuery(
    {
      id: user?.id ?? -1
    },
    {
      enabled: typeof user?.id === 'boolean'
    }
  )
  // This is intentionally separate from birthdate: "details not loaded" must
  // preserve the stored value, while "loaded without a birthdate" may clear it.
  // Both dependencies changing in the same render still runs the effect once.
  const hasUserDetails = userDetails !== undefined

  const { data: permissions } = useGetPermissions({
    customAccessToken: isUserAnonymous(data)
      ? accessTokenRef.current!
      : undefined
  })

  useEffect(() => {
    if (!data || getIsInsideIframe()) return

    const storedUser = lsGetItem(LocalStorageKey.USER)
    const validatedToken = validatedTokenRef.current
    if (
      !validatedToken ||
      validatedToken !== accessTokenRef.current ||
      (storedUser?.access_token && storedUser.access_token !== validatedToken)
    ) {
      // A storage event or token refresh replaced the session after this
      // response was requested. Never merge data validated for the old token
      // into the current user.
      return
    }

    if (isUserAnonymous(data)) {
      if (!permissions?.length) return

      const updatedUserData = {
        ...data.data.user,
        original_organization_id: data.data.original_organization_id,
        access_token: validatedToken,
        isLoggedIn: true,
        permissions: permissions ? permissions : [],
        tokenReceiveTime: Date.now(),
        user_roles: data.data.roles,
        profile: data.data.user.profile
          ? {
              ...data.data.user.profile,
              birthdate: userDetails?.data.profile?.birthdate
            }
          : null
      }
      lsSetItem(LocalStorageKey.USER, updatedUserData)
      dispatchCustomEvent(CustomEvents.AUTH_USER_DATA_UPDATED, {
        user: updatedUserData
      })

      return
    }

    const birthdate = hasUserDetails
      ? userDetails?.data.profile?.birthdate
      : (storedUser?.profile?.birthdate ?? user?.profile?.birthdate)
    const updatedUserData: UserWithToken = {
      ...user,
      ...storedUser,
      ...data.data.user,
      original_organization_id: data.data.original_organization_id,
      access_token: storedUser?.access_token ?? accessTokenRef.current!,
      refresh_token: storedUser?.refresh_token ?? refreshTokenRef.current!,
      permissions:
        permissions ??
        data.data.permissions ??
        storedUser?.permissions ??
        user?.permissions ??
        [],
      isLoggedIn: true,
      user_roles:
        storedUser?.user_roles ??
        user?.user_roles ??
        data.data.user.user_roles ??
        data.data.roles,
      profile: data.data.user.profile
        ? {
            ...data.data.user.profile,
            birthdate
          }
        : null
    }
    if (
      serializeUserForComparison(storedUser) !==
      serializeUserForComparison(updatedUserData)
    ) {
      lsSetItem(LocalStorageKey.USER, updatedUserData)
      dispatchCustomEvent(CustomEvents.AUTH_USER_DATA_UPDATED, {
        user: updatedUserData
      })
    }

    const bootstrapTokens = bootstrapSessionRef.current
    if (
      bootstrapTokens?.access_token === accessTokenRef.current &&
      bootstrapTokens.refresh_token === refreshTokenRef.current
    ) {
      if (removePromotedBootstrapTokens(bootstrapTokens)) {
        bootstrapSessionRef.current = null
      }
    }

    const currentSelectableUsers =
      lsGetItem(LocalStorageKey.SELECTABLE_USERS) ?? []
    const userSavedAlready = currentSelectableUsers.some(
      user => user.email === data.data.user.email
    )

    if (!userSavedAlready) {
      lsSetItem(LocalStorageKey.SELECTABLE_USERS, [
        ...currentSelectableUsers,
        {
          avatar:
            user?.profile?.avatar || data.data.user.profile?.avatar || null,
          email: user?.email || data.data.user.email,
          id: crypto.randomUUID(),
          name: user?.name || data.data.user.name
        }
      ])
    }
  }, [
    data,
    hasUserDetails,
    permissions,
    user,
    userDetails?.data.profile?.birthdate
  ])

  useEffect(() => {
    const unsubscribe = listenCustomEvent(
      CustomEvents.AUTH_TOKEN_UPDATED,
      event => {
        if (event.redirectRoute) {
          sessionStorage.setItem(
            SessionStorageKey.HAS_CUSTOM_LOGIN_REDIRECT,
            'true'
          )
        }
        accessTokenRef.current = event.access_token
        refreshTokenRef.current = event.refresh_token

        if (event.externalUser) {
          // Token minted by the remote's own backend — the auth service
          // cannot validate it, so adopt the supplied user directly instead
          // of rebuilding it from /checkToken. The issuing backend stays the
          // only validator: its own APIs reject the token when it goes bad.
          const externalUserData: UserWithToken = {
            ...event.externalUser,
            access_token: event.access_token,
            refresh_token: event.refresh_token ?? '',
            isLoggedIn: true,
            isExternalAuth: true,
            tokenReceiveTime: Date.now(),
            permissions: event.externalUser.permissions ?? []
          }
          bootstrapSessionRef.current = null
          setIsExternalAuth(true)
          lsSetItem(LocalStorageKey.USER, externalUserData)
          dispatchCustomEvent(CustomEvents.AUTH_USER_DATA_UPDATED, {
            user: externalUserData
          })

          // External users carry the anonymous role and are confined to
          // /public — never bounce them to the desktop route.
          if (typeof event.redirectRoute === 'string') {
            navigate(event.redirectRoute)
          }
          return
        }

        const currentUser = lsGetItem(LocalStorageKey.USER)

        if (currentUser) {
          const capturedBootstrapTokens = bootstrapSessionRef.current
          const sessionUsesCapturedBootstrap = tokenPairsMatch(
            capturedBootstrapTokens,
            currentUser
          )
          const updatedUser: UserWithToken = {
            ...currentUser,
            access_token: event.access_token,
            refresh_token: event.refresh_token,
            // A regular auth-service login always supersedes a prior
            // external session — without this, a leftover isExternalAuth
            // flag from an earlier public/external visit would keep
            // /checkToken and /permissions disabled for the real user.
            isExternalAuth: false
          }
          lsSetItem(LocalStorageKey.USER, updatedUser)
          const persistedUpdatedUser = lsGetItem(LocalStorageKey.USER)

          if (
            capturedBootstrapTokens &&
            sessionUsesCapturedBootstrap &&
            tokenPairsMatch(updatedUser, persistedUpdatedUser)
          ) {
            if (persistedUpdatedUser?.isLoggedIn) {
              removeCapturedBootstrapTokens(capturedBootstrapTokens)
              bootstrapSessionRef.current = null
            } else {
              const rotatedBootstrapTokens = {
                access_token: event.access_token,
                refresh_token: event.refresh_token
              }
              if (
                rotateCapturedBootstrapTokens(
                  capturedBootstrapTokens,
                  rotatedBootstrapTokens
                )
              ) {
                bootstrapSessionRef.current = rotatedBootstrapTokens
              } else {
                // The persisted entry was replaced between verification and
                // rotation. Drop our stale provenance rather than retaining a
                // ref that can no longer match the active session.
                bootstrapSessionRef.current = null
              }
            }
          } else if (capturedBootstrapTokens && !sessionUsesCapturedBootstrap) {
            // A bootstrap entry from another session is never ours to remove.
            bootstrapSessionRef.current = null
          }

          dispatchCustomEvent(CustomEvents.AUTH_USER_DATA_UPDATED, {
            user: updatedUser
          })
        } else {
          const bootstrapTokens = {
            access_token: event.access_token,
            refresh_token: event.refresh_token
          }
          bootstrapSessionRef.current = bootstrapTokens
          lsSetItem(LocalStorageKey.BOOTSTRAP_TOKENS, bootstrapTokens)
        }

        setIsExternalAuth(false)
        setTokenVersion(prev => prev + 1)

        if (event.redirectRoute === false) return
        if (event.redirectRoute) {
          navigate(event.redirectRoute!)
        } else {
          navigate(APP_ROUTES.desktop.path)
        }
      }
    )

    return unsubscribe
  }, [navigate])

  useEffect(() => {
    const handleStorageChange = (event: StorageEvent) => {
      if (event.storageArea && event.storageArea !== localStorage) return

      if (event.key === LocalStorageKey.USER) {
        const nextUser = parseStorageValue<UserWithToken>(event.newValue)
        if (!tokenPairsMatch(bootstrapSessionRef.current, nextUser)) {
          bootstrapSessionRef.current = null
        }
        accessTokenRef.current = nextUser?.access_token ?? null
        refreshTokenRef.current = nextUser?.refresh_token ?? null
        setUser(nextUser)
        setIsExternalAuth(!!nextUser?.isExternalAuth)
        setTokenVersion(previousVersion => previousVersion + 1)
        // null is the established cross-tab logout signal. The event contract
        // is UserWithToken | null and App clears its user context accordingly.
        dispatchCustomEvent(CustomEvents.AUTH_USER_DATA_UPDATED, {
          user: nextUser
        })
        return
      }

      if (event.key !== LocalStorageKey.BOOTSTRAP_TOKENS) return

      const nextBootstrapTokens = parseStorageValue<BootstrapTokens>(
        event.newValue
      )
      if (!nextBootstrapTokens) {
        bootstrapSessionRef.current = null
        return
      }

      const currentUser = lsGetItem(LocalStorageKey.USER)
      if (currentUser && !tokenPairsMatch(nextBootstrapTokens, currentUser)) {
        return
      }

      bootstrapSessionRef.current = nextBootstrapTokens
      accessTokenRef.current = nextBootstrapTokens.access_token
      refreshTokenRef.current = nextBootstrapTokens.refresh_token
      setIsExternalAuth(false)
      setTokenVersion(previousVersion => previousVersion + 1)
    }

    window.addEventListener('storage', handleStorageChange)
    return () => window.removeEventListener('storage', handleStorageChange)
  }, [])

  const anonUserLoggedIn = isUserAnonymous(data)
  const isUserAnonymousFromStorage = user ? isUserAnonymous(user) : false

  // External-token sessions behave like anonymous sessions: a user object
  // exists, but the session never counts as "logged in" for protected-route
  // or hybrid-redirect purposes.
  if (isExternalAuth) {
    return false
  }

  if (!isLoading && anonUserLoggedIn) {
    return false
  }

  if (!isLoading && data) {
    return !!data?.data.user
  }

  // don't return true for anonymous users during loading
  if (isUserAnonymousFromStorage) {
    return false
  }

  if (accessTokenRef.current && refreshTokenRef.current) {
    return true
  }

  if (isLoading && accessTokenRef.current && !data && user) {
    return true
  }

  if (accessTokenRef.current && user) {
    return true
  }

  return false
}
